import React, { Component } from 'react'
import { observer } from 'mobx-react'
import axios from 'axios'
import validate from 'validate.js'

import autobind from '../../lib/autobind'
import logger from '../../lib/logger'
import isToggleEnable from '../../lib/toggles'
import './styles.scss'
import '../../styles/index.scss'
import OrderNotificationModal from '../Modal/Order'
import SingleInput from './SingleInput'
import TextArea from './TextArea'
import Select from './Select'
import FormRow from './FormRow'
import States from './States.json'
import sexOptions from './sexOptions.json'
import circumstanceOptions from './circumstanceOptions.json'
import FAAFormWrapper from './FAAFormWrapper'
import FAASectionContainer from './FAASectionContainer'
import FAASubmitSection from './FAASubmitSection'
import FAAStore from '../../stores/FAA'
import DatePicker from './DatePicker'
import RadioButtonsWrapper from './RadioButtonsWrapper'
import RadioButtonGroup from './RadioButtonGroup'
import Checkbox from './Checkbox'
import FAAConfirmationMessage from './FAAConfirmationMessage'
import { phoneFormatter } from '../../lib/input-formatter'
import env from '../../env'
import FileInput from './FileInput'

class FAAForm extends Component {
  constructor (props) {
    super(props)
    autobind(this)
  }

  async getUserDetails () {
    try {
      const response = await axios({
        url: '/api/v1/user',
        method: 'get'
      })

      if (Object.entries(response.data).length) {
        const {
          user: {
            physicianId,
            medicalFacilityId,
            _id
          }
        } = response.data

        FAAStore.setField('physicianid', physicianId)
        FAAStore.setField('medicalfacilityid', medicalFacilityId)
        FAAStore.setField('userid', _id)
      }
    } catch (err) {
      logger.error(err)
    }
  }

  updateAttribute (event) {
    FAAStore.updateFieldValue([event.target.name], event.target.checked)
    FAAStore.updateFieldError([event.target.name], !event.target.checked)
  }

  handleAcknowledge (event) {
    const currentValue = !FAAStore.getValue('acknowledge')
    FAAStore.updateFieldValue('acknowledge', !currentValue)
  }

  sendForm () {
    // validating
    const valid = this.validate()
    FAAStore.setFormIsValid(valid)
    if (!valid) return

    // populating data
    const values = FAAStore.getDataValues()

    const data = []
    const phonesToClear = ['phoneNumber', 'physicianPhone', 'physicianFax']
    values.forEach(v => {
      if (phonesToClear.indexOf(v.name) >= 0) {
        data[v.name] = v.value ? v.value.replace(/\D+/g, '') : ''
      } else {
        data[v.name] = v.value
      }
    })

    FAAStore.setFormError()

    // Sending form
    FAAStore.setSendingForm()

    const payload = { ...data }
    const config = { method: 'post' }

    if (isToggleEnable(env.REACT_APP_FAA_CRM_INTEGRATION_TOGGLE)) {
      const formData = new FormData()
      formData.append('payload', JSON.stringify(payload))
      FAAStore.attachments.forEach(file => {
        formData.append('files', file)
      })
      config.url = '/api/v1/submissions'
      config.headers = {
        'Content-Type': 'multipart/form-data'
      }
      config.data = formData
    } else {
      config.url = env.REACT_APP_SEND_NOTIFICATION_ENDPOINT
      config.data = payload
    }

    axios(config).then(({ data }) => {
      FAAStore.setOrderSuccess(true)
      const confirmationNumber = isToggleEnable(env.REACT_APP_FAA_CRM_INTEGRATION_TOGGLE) ? data.faaSubmissionNumber : data.confirmationNumber
      FAAStore.setConfirmationNumber(confirmationNumber)
      FAAStore.setPreliminaryAwardApproval(data.preliminaryAwardApproval)
      FAAStore.setErrorWhenSendDocuments(data.errorSendingDocuments)
    }).catch((err) => {
      FAAStore.setOrderSuccess(false)
      logger.error(err)
      FAAStore.setFormError(true)
    }).finally(() => {
      FAAStore.setSendingForm(false)
      FAAStore.setFormSubmissionModalDisplay()
    })
  }

  isApprovalDecisionValid () {
    const { practice = false, patient = false } = FAAStore.approvalDecision
    const hasPractice = practice === true
    const hasPatient = patient === true
    FAAStore.updateFieldError('approvalDecision', false)
    FAAStore.updateFieldError('approvalPatientCom', false)
    FAAStore.updateFieldError('approvalPracticeCom', false)

    if (!hasPatient && !hasPractice) {
      FAAStore.updateFieldError('approvalDecision')
      return false
    }

    if (hasPatient && !FAAStore.getValue('approvalPatientCom')) {
      FAAStore.updateFieldError('approvalPatientCom')
      return false
    }

    if (hasPractice && !FAAStore.getValue('approvalPracticeCom')) {
      FAAStore.updateFieldError('approvalPracticeCom')
      return false
    }

    return true
  }

  validate () {
    let valid = true
    if (FAAStore.getValue('signatureText')) FAAStore.updateFieldError('signatureText', false)
    FAAStore.REQUIRED_FIELDS.forEach(field => {
      const currentValue = FAAStore.getValue(field)
      if (field === 'signatureText' && FAAStore.getValue('signatureText')) FAAStore.updateFieldError('signatureText', false)

      if (!currentValue || FAAStore.hasError(field)) {
        if (!FAAStore.hasError(field)) FAAStore.updateFieldError(field)
        valid = false
      }

      if (field === 'who' && currentValue === 'Representative') {
        valid = !this.whoRepresentativeErrorHandling() ? this.whoRepresentativeErrorHandling() : valid
      }
    })
    if (!this.isApprovalDecisionValid()) valid = false

    // get PATIENT contact preference
    const patientApproval = FAAStore.getValue('approvalPatientCom')
    if (patientApproval !== '') {
      // if phone is selected we need to validate it
      if (patientApproval === 'phone') {
        let validatePhoneNumber = FAAStore.getValue('phoneNumber')
        validatePhoneNumber = validatePhoneNumber.replace(/\D+/g, '')
        if (Object.prototype.hasOwnProperty.call(validatePhoneNumber, 'length') && (validatePhoneNumber.length < 10 || validatePhoneNumber.length > 10)) {
          FAAStore.updateFieldError('phoneNumber')
          valid = false
        }
      }

      // if email is selected we need to validate it
      if (patientApproval === 'email') {
        const validateEmail = FAAStore.getValue('email')
        if (!this.emailValidated('email', validateEmail)) {
          FAAStore.updateFieldError('email')
          valid = false
        }
      }
    }

    // get PARTNER contact preference
    const partnerApproval = FAAStore.getValue('approvalPracticeCom')
    if (partnerApproval !== '') {
      // if partner fax is selected we need to validate it
      if (partnerApproval === 'fax') {
        let validatePhysicianFax = FAAStore.getValue('physicianFax')
        validatePhysicianFax = validatePhysicianFax.replace(/\D+/g, '')
        if (Object.prototype.hasOwnProperty.call(validatePhysicianFax, 'length') && (validatePhysicianFax.length > 10 || validatePhysicianFax.length < 10)) {
          FAAStore.updateFieldError('physicianFax')
          valid = false
        } else {
          FAAStore.updateFieldError('physicianFax', false)
        }
      }

      // if partner email is selected we need to validate it
      if (partnerApproval === 'email') {
        const validatePhysicianEmail = FAAStore.getValue('physicianEmail')
        if (!this.emailValidated('email', validatePhysicianEmail)) {
          FAAStore.updateFieldError('physicianEmail')
          valid = false
        } else {
          FAAStore.updateFieldError('physicianEmail', false)
        }
      }

      // facility and physician are always required if 'practice' is selected
      if (FAAStore.getValue('facility') === '') {
        FAAStore.updateFieldError('facility')
        valid = false
      }
      if (FAAStore.getValue('orderingPhysician') === '') {
        FAAStore.updateFieldError('orderingPhysician')
        valid = false
      }
    }

    return valid
  }

  whoRepresentativeErrorHandling () {
    let valid = true
    const whoOptions = ['whoFirst', 'whoLast', 'whoRelation']
    whoOptions.forEach((f) => {
      if (!FAAStore.getValue(f)) {
        FAAStore.updateFieldError(f)
        valid = false
      }
    })
    return valid
  }

  isNotEmptyField (name, value) {
    const currentValue = FAAStore.getValue(name)
    if (currentValue.length === 0) {
      FAAStore.updateFieldError(name)
      return false
    }
    FAAStore.updateFieldError(name, false)
    return true
  }

  preventLeadingZero (name) {
    let [currentValue] = FAAStore.getValue(name)
    if (currentValue === '0') {
      FAAStore.updateFieldValue(name, '')
    }
    [currentValue] = FAAStore.getValue(name)
    return currentValue !== '0'
  }

  preventMoreThanTwenty (name) {
    let currentValue = +FAAStore.getValue(name)
    if (currentValue >= 20) {
      FAAStore.updateFieldValue(name, '20')
    }
    return true
  }

  zipCodeValidated (name, value) {
    if(name === 'facilityPostalCode' && value === '') return true

    const zipCodeValidation = {
      zipcode: {
        format: {
          pattern: /\d{5}(-\d{4})?/
        }
      }
    }
    if (validate({ zipcode: value }, zipCodeValidation)) return false
    return true
  }

  numbersValidated (name, value) {
    const numbersValidation = /^\d+$/
    return numbersValidation.test(value)
  }

  emailValidated (name, value) {
    const emailValidation = { email: { email: true } }
    if (validate({ email: value }, emailValidation)) return false
    return true
  }

  onlyNumbersAllowed (e) {
    const numbersAllowed = /^\d+$/
    if (!numbersAllowed.test(e.key)) e.preventDefault()
  }

  render () {
    return (
      <FAAFormWrapper>
        <form id='main-form'>
          <FAASectionContainer title='Patient Information'>
            <FormRow colsSizes={[5, 2, 5]}>
              <SingleInput
                inputType='text'
                title='First Name'
                name='firstName'
                placeholder='First Name'
                maxLength='50'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='firstName'
                Store={FAAStore}
              />
              <SingleInput
                inputType='text'
                title='Middle Name'
                name='middleName'
                placeholder='MI'
                maxLength='1'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='middleName'
                Store={FAAStore}
              />
              <SingleInput
                inputType='text'
                title='Last Name'
                name='lastName'
                placeholder='Last Name'
                maxLength='50'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='lastName'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow>
              <Select
                name='sex'
                placeholder='Sex'
                placeholderValue='Sex'
                options={sexOptions}
                className='select2-elm'
                Store={FAAStore}
                automationName='sex'
              />

              <DatePicker
                title='Date of Birth'
                name='patientDateOfBirth'
                placeholderText='Date Of Birth'
                automationName='dateOfBirth'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow extraClasses={['', 'col-xs-12']}>
              <SingleInput
                inputType='text'
                title='Street Address'
                name='streetAddress'
                placeholder='Street Address'
                maxLength='100'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='streetAddress'
                Store={FAAStore}
              />
              <SingleInput
                inputType='text'
                title='City'
                name='city'
                placeholder='City'
                maxLength='50'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='city'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow className='multi-input' extraClasses={['col-xs-12', 'col-xs-12']}>
              <Select
                name='state'
                className='select2-elm'
                id='patient-addr-state'
                placeholder='State'
                placeholderValue=''
                options={States}
                Store={FAAStore}
                automationName='state'
              />
              <SingleInput
                inputType='text'
                title='Zipcode'
                name='zipCode'
                placeholder='Zipcode'
                maxLength='10'
                onChangeValidationFunction={[this.zipCodeValidated, this.isNotEmptyField]}
                automationName='zipCode'
                Store={FAAStore}
                trimSpaces={true}
              />
            </FormRow>

            <div className='row phone-section'>
              <div className='col-md-5 col-xs-12'>
                <SingleInput
                  title='Phone Number'
                  name='phoneNumber'
                  placeholder='Phone Number'
                  onChangeValidationFunction={[this.isNotEmptyField]}
                  automationName='phoneNumber'
                  Store={FAAStore}
                  formatter={phoneFormatter}
                />
              </div>
              <div className='checkbox-inline col-md-7 col-xs-12 checkOne'>
                <input
                  type='checkbox'
                  name='phoneAuthorization'
                  onChange={(e) => {
                    this.updateAttribute(e)
                  }}
                  checked={FAAStore.phoneAuthorization.value}
                  data-automation-name='phoneAuthorization'
                />
                <label className='radioLabel'>I authorize Foundation Medicine to leave a detailed voicemail at this
                  phone number
                </label>
              </div>
            </div>

            <FormRow colDefaultSize={12}>
              <SingleInput
                inputType='text'
                title='Email'
                name='email'
                placeholder='Email Address'
                maxLength='50'
                automationName='patientEmail'
                Store={FAAStore}
              />
            </FormRow>
          </FAASectionContainer>

          <FAASectionContainer title='Ordering Physician And Facility Information'>
            <FormRow colDefaultSize={12}>
              <SingleInput
                inputType='text'
                title='Facility'
                name='facility'
                placeholder='Facility'
                maxLength='30'
                automationName='facility'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow extraClasses={['', 'col-xs-12']}>
              <SingleInput
                inputType='text'
                title='Street Address'
                name='facilityStreetAddress'
                placeholder='Street Address'
                maxLength='100'
                automationName='facilityStreetAddress'
                Store={FAAStore}
              />
              <SingleInput
                inputType='text'
                title='City'
                name='facilityCity'
                placeholder='City'
                maxLength='50'
                automationName='facilityCity'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow className='multi-input' extraClasses={['col-xs-12', 'col-xs-12']}>
              <Select
                name='facilityStateOrProvince'
                className='select2-elm'
                id='patient-addr-state'
                placeholder='State'
                placeholderValue=''
                options={States}
                Store={FAAStore}
                automationName='facilityStateOrProvince'
              />
              <SingleInput
                inputType='text'
                title='Facility Postal Code'
                name='facilityPostalCode'
                placeholder='Facility Postal Code'
                maxLength='10'
                onChangeValidationFunction={[this.zipCodeValidated]}
                automationName='facilityPostalCode'
                Store={FAAStore}
                trimSpaces={true}
              />
            </FormRow>

            <FormRow colDefaultSize={12}>
              <SingleInput
                inputType='text'
                title='Ordering Physician'
                name='orderingPhysician'
                placeholder='Ordering Physician'
                maxLength='30'
                max='30'
                automationName='orderingPhysician'
                Store={FAAStore}
              />
            </FormRow>

            <FormRow>
              <SingleInput
                title='physician Phone Number'
                name='physicianPhone'
                placeholder='Phone Number'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='physicianPhone'
                Store={FAAStore}
                formatter={phoneFormatter}
              />
              <SingleInput
                title='Physician Fax'
                name='physicianFax'
                placeholder='Fax Number'
                onChangeValidationFunction={[this.isNotEmptyField]}
                automationName='physicianFax'
                Store={FAAStore}
                formatter={phoneFormatter}
              />
            </FormRow>

            <FormRow colDefaultSize={12}>
              <SingleInput
                inputType='text'
                title='Physician Email'
                name='physicianEmail'
                placeholder='Email Address'
                maxLength='50'
                automationName='physicianEmail'
                Store={FAAStore}
              />
            </FormRow>
          </FAASectionContainer>

          <FAASectionContainer title='Total Gross Annual Household Income & Family Size'>
            <FormRow colDefaultSize={12} extraClasses={['col-lg-12 col-md-12']}>
              <div className='input-group'>
                <span className='input-group-addon'>$</span>
                <SingleInput
                  inputType='text'
                  title='Total Gross'
                  name='totalGross'
                  placeholder='Please list current gross annual household income (estimated ranges not accepted).'
                  maxLength='10'
                  onChangeValidationFunction={[this.numbersValidated, this.isNotEmptyField]}
                  onKeyPressValidationFunction={[this.onlyNumbersAllowed]}
                  automationName='totalGross'
                  Store={FAAStore}
                />
              </div>
            </FormRow>
            <div className='row'>
              <div id='household-persons-label' className='col-lg-8 col-md-8'>
                <label className='bold'>Number of family members in household supported by above gross annual household
                  income (including patient)
                </label>
              </div>
              <div className='col-lg-4 col-md-4 form-group check-error'>
                <SingleInput
                  inputType='number'
                  title='Family Members'
                  name='familyMembers'
                  placeholder='Family Members'
                  onChangeValidationFunction={[this.numbersValidated, this.isNotEmptyField, this.preventLeadingZero, this.preventMoreThanTwenty]}
                  onKeyPressValidationFunction={[this.onlyNumbersAllowed]}
                  min='1'
                  max='20'
                  maxLength='2'
                  automationName='familyMembers'
                  Store={FAAStore}
                />
              </div>
            </div>
          </FAASectionContainer>

          <FAASectionContainer
            title='Please Advise Of Any Extenuating Circumstances That You Would Like Us To Consider'
          >
            <p className='text-center'>(Please advise of any extenuating circumstances that you would like us to
              consider. Select all that apply)
            </p>
            <div className='form-group check-error'>
              <label className='sr-only'>Extenuating Circumstances</label>
              <Select
                name='circumstance'
                placeholder='Select'
                options={circumstanceOptions}
                className='select2-elm'
                isMultiple
                simpleValue
                delimiter=','
                joinValues
                onChangeValidationFunction={[this.isNotEmptyField]}
                Store={FAAStore}
                id='circumstance'
                automationName='circumstance'
              />
            </div>
            { FAAStore.receiveCharityCareSelected &&
              <div className='form-group extra-infos'>
                  <FileInput
                      name='documentUploader'
                      automationName='documentUploader'
                      Store={FAAStore}
                    />
              </div>
            }
            {
              ((FAAStore.permanentLossSelected && FAAStore.temporaryLossSelected) || FAAStore.otherCircumstancesSelected) &&
                <div className='form-group check-error extra-infos'>
                  <label className='sr-only'>Other Extenuating Circumstances</label>
                  <TextArea
                    name='otherCircumstances'
                    placeholder='Please include additional detail (e.g. if both temporary and permanent loss of income are selected, if other circumstances is selected, etc.)'
                    className='form-control'
                    rows={3}
                    automationName='otherCircumstances'
                    Store={FAAStore}
                  />
                </div>
            }
          </FAASectionContainer>

          <FAASectionContainer title='Who should we contact with the approval decision?'>
            <p className='text-center'>(Ensure contact information for patient and practice is filled in at the top of
              this form)
            </p>

            <RadioButtonsWrapper>
              <div className='checkTwoWrap'>
                <Checkbox
                  title='Patient'
                  value='patient'
                  name='approvalDecision'
                  isChecked={FAAStore.approvalDecision.patient}
                  onChange={e => FAAStore.setApprovalDecisionPatient(e.target.checked)}
                  automationName='patientError'
                  Store={FAAStore}
                />
                <Checkbox
                  title='Practice'
                  value='practice'
                  name='approvalDecision'
                  isChecked={FAAStore.approvalDecision.practice}
                  onChange={e => FAAStore.setApprovalDecisionPractice(e.target.checked)}
                  automationName='practiceError'
                  Store={FAAStore}
                />
              </div>
            </RadioButtonsWrapper>

            {
              FAAStore.approvalDecision.patient
                ? (
                  <div id='approval-decision-patient-extra'>
                    <h5>What's the patient's preferred method of communication?</h5>
                    <div className='checkThreeWrap'>
                      <RadioButtonGroup
                        options={[
                          { title: 'Phone', value: 'phone' },
                          { title: 'Email', value: 'email' },
                          { title: 'Mail', value: 'mail' }
                        ]}
                        name='approvalPatientCom'
                        wrapperClass='col-md-4 col-xs-12'
                        automationName='patientPrefError'
                        Store={FAAStore}
                      />
                    </div>
                  </div>
                ) : null
            }

            {
              FAAStore.approvalDecision.practice &&
                <div id='approval-decision-practice-extra'>
                  <h5>What's the practice's preferred method of communication?</h5>
                  <div className='checkFourWrap'>
                    <RadioButtonGroup
                      options={[
                        { title: 'Email', value: 'email' },
                        { title: 'Fax', value: 'fax' }
                      ]}
                      name='approvalPracticeCom'
                      wrapperClass='col-md-6 col-xs-12'
                      automationName='practicePrefError'
                      Store={FAAStore}
                    />
                  </div>
                </div>
            }

            <hr />
            <div className='checkTwoWrap padBottom'>
              <RadioButtonGroup
                name='who'
                options={[
                  { title: 'I am the patient', value: 'Patient' },
                  { title: 'I am representing the patient', value: 'Representative' }
                ]}
                wrapperClass='col-md-6 col-xs-12'
                automationName='who'
                Store={FAAStore}
              />
            </div>

            {
              FAAStore.getValue('who') === 'Representative' &&
                <FormRow colsSizes={[6, 6, 12]}>
                  <SingleInput
                    inputType='text'
                    title='First Name'
                    name='whoFirst'
                    overrideId
                    id='who-first-name'
                    placeholder='First Name'
                    onChangeValidationFunction={[this.isNotEmptyField]}
                    maxLength='50'
                    automationName='who-first-name'
                    Store={FAAStore}
                  />
                  <SingleInput
                    inputType='text'
                    title='Last Name'
                    name='whoLast'
                    overrideId
                    id='who-last-name'
                    placeholder='Last Name'
                    onChangeValidationFunction={[this.isNotEmptyField]}
                    maxLength='50'
                    automationName='who-last-name'
                    Store={FAAStore}
                  />
                  <SingleInput
                    inputType='text'
                    title='Relationship to Patient'
                    name='whoRelation'
                    overrideId
                    id='patient-relationshipe'
                    placeholder='Relationship to Patient'
                    onChangeValidationFunction={[this.isNotEmptyField]}
                    maxLength='30'
                    automationName='patient-relationshipe'
                    Store={FAAStore}
                  />
                </FormRow>
            }
          </FAASectionContainer>

          <FAASectionContainer title='Acknowledge and sign in the signature box below'>
            <div>
              <div className='row'>
                <div className='col-xs-12 checkbox-inline wrapText'>
                  <div
                    className={FAAStore.getValue('acknowledge') ? 'checkOne checked' : 'checkOne'}
                    onClick={this.handleAcknowledge}
                  >
                    <label
                      data-automation-name='acknowledgeError'
                      className={FAAStore.hasError('acknowledge') ? 'faa-checkbox--label has-error long-message-checkbox' : 'radioLabel long-message-checkbox'}
                    >
                      <input
                        type='checkbox'
                        id='acknowledge'
                        name='acknowledge'
                        value={FAAStore.getValue('acknowledge')}
                        onChange={(e) => {
                          this.updateAttribute(e)
                        }}
                        checked={FAAStore.acknowledge.value}
                        data-automation-name='acknowledge'
                        className=''
                      />
                      I attest that I do not have enough resources or assets to cover the cost of Foundation Medicine
                      testing while also fulfilling my current financial obligations and liabilities. I understand that
                      Foundation Medicine may contact me to verify the information provided in this financial assistance
                      application. I affirm that the information I have provided is true and accurate to the best of my
                      knowledge. I agree to notify Foundation Medicine if my financial situation changes and I no longer need
                      financial assistance. I understand that if any information I provide is found to be false, my financial
                      assistance will be denied, and I will be responsible for paying for the services provided.
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </FAASectionContainer>

          <FormRow className='hs-form-field'>
            <SingleInput
              className='form-control col-md-6'
              inputType='text'
              name='signatureText'
              placeholder='Type your First and Last Name to sign'
              automationName='signatureText'
              Store={FAAStore}
            />
            <DatePicker
              title='signatureDate'
              name='signatureDate'
              placeholderText='Signature date'
              automationName='signatureDate'
              Store={FAAStore}
            />
          </FormRow>

          <p className='small' data-automation-name='patientrepacknowledgementmessage'>
            * As a Representative of the patient, or an Ordering Physician completing this application on the patient’s
            behalf, my signature certifies that I have explained to the patient the nature and purpose of this
            application and that the patient has consented to my completing the application on his/her behalf. My
            signature also indicates that, at the time of application, the patient named above was unable to sign this
            form, and that, should an authorized personal representative have been appointed, no such authorized
            personal representative was available or willing to sign on the patient’s behalf. My signature is not an
            acceptance of financial responsibility or liability for the services rendered.
          </p>
          
          <p className='footer' data-automation-name='footer'>
            For patients with health insurance, the insurance provider will be billed first. Your qualified amount for financial assistance will be applied to any unpaid balance remaining after billing insurance. Financial assistance is not available for patients who have health insurance but are choosing to pay for testing themselves.
          </p>

          <FAASubmitSection
            sendForm={this.sendForm}
            resetForm={FAAStore.cleanData}
            valid={FAAStore.isValid}
            automationName='submit'
            Store={FAAStore}
          />

          {
            FAAStore.confirmationNumber !== '' &&
            FAAStore.valid &&
            env.REACT_APP_FAA_CRM_INTEGRATION_TOGGLE !== 'on' &&
              <FAAConfirmationMessage Store={FAAStore} />
          }

          <div className='callback' style={{ visibility: 'hidden', opacity: 0 }} />
          <input type='hidden' name='trap' defaultValue='human' />
        </form>
        {
          env.REACT_APP_FAA_CRM_INTEGRATION_TOGGLE === 'on' &&
            <OrderNotificationModal
              store={FAAStore}
              show={FAAStore.modalDisplay}
              success={FAAStore.orderSuccess}
            />
        }
      </FAAFormWrapper>
    )
  }
}

export default observer(FAAForm)
